import { action } from '@play-co/replicant';
import createActions from '../config/actions/createActions';
import {
  ChatbotGroup,
  incentivizedOAStartLevel,
  incentivizedOAAdditionGemsReward,
  type IncentivizedOARewardClaimResult,
  resubscriptionGemsReward,
} from './chatbot.ruleset';
import * as Inventory from '../inventory';
import { Feature } from '../analytics';
import {
  canClaimResubscriptionReward,
  hasClaimedIncentivizedOAReward,
} from './chatbot.getters';

export const chatbotActions = createActions({
  updateChatbotGroup: action((state, group: ChatbotGroup, api) => {
    state.chatbot.group = group;
  }),

  /**
   * Atomically grant the verified lifetime OA reward after Puzzle 5.
   *
   * @returns The claim outcome for the calling OA surface.
   */
  claimIncentivizedOAAdditionReward: action(
    (state, _, api): IncentivizedOARewardClaimResult => {
      // Players must complete Puzzle 5 before claiming the OA reward.
      const isEligibleTreatment =
        state.match2.lastLevel >= incentivizedOAStartLevel;
      if (!isEligibleTreatment) return 'ineligible';

      // The legacy claim marker is the permanent source of truth for all OA rewards.
      if (hasClaimedIncentivizedOAReward(state)) return 'already_claimed';

      // Record the lifetime claim before granting so repeated calls are idempotent.
      state.chatbot.resubscriptionState = 'claimed';

      Inventory.modifiers.addFreeGems(
        state,
        incentivizedOAAdditionGemsReward,
        {
          feature: Feature.RESUBSCRIBE._,
          $subFeature: Feature.RESUBSCRIBE.INCENTIVIZED_OA_REWARD,
        },
        api,
      );

      // The client verified friendship and the reward was granted.
      return 'granted';
    },
  ),

  triggerResubscriptionPromptCooldown: action((state, _, api) => {
    state.chatbot.resubscriptionPromptTimestamp = api.date.now();
  }),

  triggerResubscriptionIntent: action((state, _, api) => {
    if (state.chatbot.resubscriptionState) {
      throw new Error('Subscription intent already triggered');
    }

    state.chatbot.resubscriptionState = 'pending';
  }),

  /** Increments the all-time resubscribe popup view counter. */
  incrementResubscribePopupViewCount: action((state) => {
    state.chatbot.resubscribePopupViewCount++;
  }),

  /** Increments the all-time OA Add-Friend overlay view counter. */
  incrementAddFriendPopupViewCount: action((state) => {
    state.chatbot.addFriendPopupViewCount++;
  }),

  /** Increments the all-time profile-consent popover view counter. */
  incrementProfileConsentPopupViewCount: action((state) => {
    state.chatbot.profileConsentPopupViewCount++;
  }),

  claimResubscriptionReward: action((state, _, api) => {
    const now = api.date.now();

    if (!canClaimResubscriptionReward(state, now)) {
      throw new Error('Cannot claim subscription reward');
    }

    state.chatbot.resubscriptionState = 'claimed';
    Inventory.modifiers.addFreeGems(
      state,
      resubscriptionGemsReward,
      {
        feature: Feature.RESUBSCRIBE._,
        $subFeature: Feature.RESUBSCRIBE._ + '_prompt',
      },
      api,
    );
  }),
});
